import { supabase } from "@/integrations/supabase/client";
import {
  DEFAULT_SIZE_SETTINGS,
  DEFAULT_COMPLEXITY_MULTIPLIERS,
  DEFAULT_STYLE_MULTIPLIERS,
  DEFAULT_BODY_PART_MULTIPLIERS,
} from "@/types/pricing";

export async function ensurePricingSettings(artistId: string): Promise<void> {
  const { data: existing } = await supabase
    .from("pricing_settings")
    .select("id")
    .eq("artist_id", artistId)
    .maybeSingle();

  if (existing?.id) return;

  const { data: pricing, error: pricingError } = await supabase
    .from("pricing_settings")
    .insert({ artist_id: artistId })
    .select("id")
    .single();

  if (pricingError || !pricing?.id) {
    console.error("Failed to create pricing_settings:", pricingError);
    return;
  }

  try {
    await Promise.all([
      supabase
        .from("size_settings")
        .insert(DEFAULT_SIZE_SETTINGS.map((s) => ({ ...s, pricing_settings_id: pricing.id }))),
      supabase
        .from("complexity_multipliers")
        .insert(DEFAULT_COMPLEXITY_MULTIPLIERS.map((c) => ({ ...c, pricing_settings_id: pricing.id }))),
      supabase
        .from("style_multipliers")
        .insert(DEFAULT_STYLE_MULTIPLIERS.map((s) => ({ ...s, pricing_settings_id: pricing.id }))),
      supabase
        .from("body_part_multipliers")
        .insert(DEFAULT_BODY_PART_MULTIPLIERS.map((b) => ({ ...b, pricing_settings_id: pricing.id }))),
    ]);
  } catch (e) {
    console.error("Failed to create default multipliers:", e);
  }
}

// Creates artist_profiles if missing, then seeds pricing + defaults.
// Returns the artist_profiles.id or null on failure.
export async function ensureArtistSetup(userId: string, artistName: string): Promise<string | null> {
  const { data: existing } = await supabase
    .from("artist_profiles")
    .select("id")
    .eq("user_id", userId)
    .maybeSingle();

  let artistId = existing?.id ?? null;

  if (!artistId) {
    const { data: newProfile, error: insertError } = await supabase
      .from("artist_profiles")
      .insert({ user_id: userId, artist_name: artistName })
      .select("id")
      .single();

    if (insertError || !newProfile?.id) {
      console.error("Failed to create artist_profiles:", insertError);
      return null;
    }
    artistId = newProfile.id;
  }

  await ensurePricingSettings(artistId);
  return artistId;
}
